import { routing } from "./routing";

export { routing } from "./routing";

export type Locale = (typeof routing.locales)[number];

/**
 * App-scope for `loadMessages` — selects which message namespace bundle
 * the consumer pulls.
 *
 * Phase-7 namespace split (ADR-026 §6 #7) keeps each app's i18n bundle
 * focused on the surfaces it actually renders:
 *   - "marketing" → common (auth/common/status/generator) + marketing
 *     (marketing/help) — marketing/.
 *   - "app"       → common (auth/common/status/generator) + app
 *     (app/onboarding) — web/.
 *
 * The `common` bundle is shared because both apps surface auth labels
 * (Sign-up / Login chips), the StatusBanner, and the @qrody/qr-generator
 * (which reads `generator.*` translations).
 */
export type MessageScope = "marketing" | "app";

// Locale + scope-keyed message loader. Apps wire this into next-intl's
// getRequestConfig — keeping the dynamic import inside the package means
// the messages JSON only ships in bundles that import @qrody/i18n with
// the matching scope.
export async function loadMessages(
  locale: string,
  scope: MessageScope,
): Promise<Record<string, unknown>> {
  const safeLocale = locale === "de" || locale === "en" ? locale : "de";

  const common = await loadCommon(safeLocale);
  const surface =
    scope === "marketing"
      ? await loadMarketing(safeLocale)
      : await loadApp(safeLocale);

  return { ...common, ...surface };
}

async function loadCommon(locale: "de" | "en"): Promise<Record<string, unknown>> {
  switch (locale) {
    case "de":
      return (await import("./messages/common/de.json")).default;
    case "en":
      return (await import("./messages/common/en.json")).default;
  }
}

async function loadMarketing(
  locale: "de" | "en",
): Promise<Record<string, unknown>> {
  switch (locale) {
    case "de":
      return (await import("./messages/marketing/de.json")).default;
    case "en":
      return (await import("./messages/marketing/en.json")).default;
  }
}

async function loadApp(locale: "de" | "en"): Promise<Record<string, unknown>> {
  switch (locale) {
    case "de":
      return (await import("./messages/app/de.json")).default;
    case "en":
      return (await import("./messages/app/en.json")).default;
  }
}
